import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  Snowflake, Star, Heart, ArrowRight } from 'lucide-react';

const UMinnesota: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "University of Minnesota";

    return (
        <div className="min-h-screen bg-white font-sans text-[#5d0013] selection:bg-[#ffcc33] selection:text-[#5d0013]">
            {/* Top Bar - Gold */}
            <div className="bg-[#ffcc33] text-[#5d0013] py-2 px-6 flex justify-between items-center text-xs font-bold uppercase tracking-wide">
                <div className="flex gap-4">
                    <a href="#" className="hover:underline">Campuses</a>
                    <a href="#" className="hover:underline">Twin Cities</a>
                </div>
                 <div className="flex gap-6">
                    <a href="#" className="hover:underline">One Stop</a>
                    <a href="#" className="hover:underline">MyU</a>
                    <a href="#" className="hover:underline">Maps</a>
                </div>
            </div>

            {/* Nav */}
            <nav className="bg-[#5d0013] py-6 px-6 md:px-12 shadow-lg">
                <div className="max-w-[1240px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3">
                         <div className="bg-[#ffcc33] text-[#5d0013] font-serif font-black text-2xl w-10 h-10 flex items-center justify-center rounded-sm">{schoolName.charAt(0)}</div>
                         <div className="flex flex-col">
                            <span className="font-serif text-lg font-bold text-white uppercase tracking-wide leading-none">{schoolName}</span>
                            <span className="font-serif text-xs font-bold text-[#ffcc33] uppercase tracking-wide leading-none">Driven to Discover</span>
                         </div>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-white text-sm uppercase tracking-wide">
                        <a href="#" className="hover:text-[#ffcc33] transition-colors">Admissions</a>
                        <a href="#" className="hover:text-[#ffcc33] transition-colors">Academics</a>
                        <a href="#" className="hover:text-[#ffcc33] transition-colors">Research</a>
                        <a href="#" className="hover:text-[#ffcc33] transition-colors">About</a>
                        <a href="#" className="hover:text-[#ffcc33] transition-colors">Give</a>
                    </div>
                    
                    <div className="flex gap-4 items-center">
                         <Search className="cursor-pointer text-white hover:text-[#ffcc33]" size={22} />
                         <Menu className="cursor-pointer lg:hidden text-white" size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - Mall */}
            <header className="relative h-[600px]">
                <img 
                    src="https://images.unsplash.com/photo-1548625361-98782ee88fc8?q=80&w=2070&auto=format&fit=crop" 
                    alt="Northrop Mall" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                 {/* Maroon bottom fade */}
                <div className="absolute bottom-0 w-full h-1/2 bg-gradient-to-t from-[#5d0013] to-transparent"></div>

                <div className="absolute bottom-0 left-0 w-full p-12 md:p-20 text-center">
                     <div className="max-w-4xl mx-auto text-white">
                         <h1 className="text-4xl md:text-6xl font-serif font-bold mb-6">
                             Driven to Discover.
                         </h1>
                         <p className="text-xl md:text-2xl mb-10 font-light text-gray-200">
                             We are pushing the boundaries of knowledge to change the world.
                         </p>
                         <div className="flex justify-center gap-4">
                             <Button className="bg-[#ffcc33] hover:bg-[#ffdb4d] text-[#5d0013] font-bold uppercase tracking-widest px-10 h-12 rounded-sm">
                                 Apply
                             </Button>
                              <Button className="bg-transparent border-2 border-white hover:bg-white hover:text-[#5d0013] text-white font-bold uppercase tracking-widest px-10 h-12 rounded-sm">
                                 Visit
                             </Button>
                         </div>
                     </div>
                </div>
            </header>

            {/* Stats / Goldy */}
            <section className="bg-white py-24 px-6 md:px-12">
                <div className="max-w-[1240px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center text-[#5d0013]">
                     <div className="bg-gray-50 p-8 rounded-lg">
                         <Star size={48} className="text-[#ffcc33] mx-auto mb-6" />
                         <h3 className="text-2xl font-bold mb-4">World Class</h3>
                         <p className="text-gray-600">Top Public Research University</p>
                     </div>
                      <div className="bg-gray-50 p-8 rounded-lg">
                         <Heart size={48} className="text-[#ffcc33] mx-auto mb-6" />
                         <h3 className="text-2xl font-bold mb-4">Health</h3>
                         <p className="text-gray-600">Leader in Medical Innovation</p>
                     </div>
                      <div className="bg-gray-50 p-8 rounded-lg">
                         <Snowflake size={48} className="text-[#ffcc33] mx-auto mb-6" />
                         <h3 className="text-2xl font-bold mb-4">Resilient</h3>
                         <p className="text-gray-600">Thriving in the Bold North</p>
                     </div>
                </div>
            </section>

             {/* News Blocks */}
             <section className="bg-[#f2f2f2] py-24 px-6 md:px-12">
                 <div className="max-w-[1240px] mx-auto">
                     <div className="flex justify-between items-end mb-12 border-b-2 border-[#5d0013] pb-4">
                         <h2 className="text-3xl font-serif font-bold text-[#5d0013]">Latest {schoolName} News</h2>
                         <a href="#" className="font-bold text-[#5d0013] text-xs uppercase hover:underline mb-2">Newsroom</a>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                         {[
                             "Medical school receives historic grant for cancer research",
                             "New data science center opens on East Bank",
                             "Gopher women's hockey wins national championship"
                         ].map((title, i) => (
                             <div key={i} className="bg-white shadow-sm p-6 group cursor-pointer border-t-4 border-[#ffcc33]">
                                 <span className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-3 block">Research</span>
                                 <h3 className="text-xl font-bold text-[#5d0013] group-hover:text-[#b00020] transition-colors mb-4 leading-tight">{title}</h3>
                                 <p className="text-gray-600 text-sm mb-4">Lorem ipsum dolor sit amet, consectetur adipiscing elit.</p>
                                  <div className="flex items-center gap-2 text-[#5d0013] font-bold uppercase text-xs tracking-widest">
                                     Read More <ArrowRight size={14}/>
                                 </div>
                             </div>
                         ))}
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#333] text-gray-400 py-16 px-6 md:px-12">
                  <div className="max-w-[1240px] mx-auto flex flex-col md:flex-row justify-between gap-12 font-sans text-sm">
                      <div className="max-w-xs">
                           <div className="flex items-center gap-2 mb-6">
                               <div className="bg-[#ffcc33] text-[#5d0013] font-black w-8 h-8 flex items-center justify-center rounded-sm text-lg">{schoolName.charAt(0)}</div>
                               <span className="font-serif font-bold text-white text-lg">{schoolName}</span>
                           </div>
                          <p className="mb-6 opacity-80">
                              Minneapolis, MN 55455 <br/>
                              612-625-5000
                          </p>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 font-bold uppercase text-xs tracking-widest">
                          <ul className="space-y-4 hover:[&_a]:text-white">
                              <li><a href="#">Directories</a></li>
                              <li><a href="#">Contact {schoolName.split(' ')[0]}</a></li>
                              <li><a href="#">Privacy</a></li>
                          </ul>
                          <ul className="space-y-4 hover:[&_a]:text-white">
                               <li><a href="#">Maps & Directions</a></li>
                              <li><a href="#">Jobs</a></li>
                              <li><a href="#">MyU</a></li>
                          </ul>
                      </div>
                  </div>
                  <div className="max-w-[1240px] mx-auto mt-16 text-center text-xs text-gray-600 border-t border-gray-700 pt-8">
                      © 2026 Regents of the {schoolName}
                  </div>
             </footer>
        </div>
    );
};

export default UMinnesota;
